import React, { useState } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import '../../../App.css';
import { apiUrlPrefix } from '../../../config';
import useStore from '../state-files/zustondStoreStates';
import GetUserCommunities from '../Components/getUserCommunities';
import GetUsers from '../Components/getUsers';
import getAllCommunityUsers from '../helper-methods/getAllCommunityUsers';

const RemoveCommunityMember = ({ closeMenu }) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setName] = useState();
  const { setCommunities } = useStore(); // Use zustand store
  const { currentCommunityId, currentCommunityName } = useStore(); // Use zustand store

  // Use TanStack Query to manage the user list
  const { data: usersData } = useQuery({
    queryKey: ['allUsers'],
    queryFn: () => getAllCommunityUsers(setName, currentCommunityId),
    enabled: open, // Only fetch when the dialog is opened
  });

  // --- MUTATION LOGIC ---
  const removeCommunityMember = async ({ users, communities }) => {
    const apiUrl = apiUrlPrefix + 'Communities/removeCommunityMember';
       
    // 1. Map the names (from Formik) back to IDs (from usersData)
    const userIds = users.map(selectedName => {
      // If 'All' is selected, handle it based on your backend logic 
      // (usually you'd send a special ID or the string 'All')
      if (selectedName === 'All') return 'All';
      if (selectedName === currentUser) return localStorage.getItem('userId');

      // Find the original user object that matches the selected name
      const foundUser = usersData.find(u => u.name === selectedName);

      // Return the ID if found, otherwise return the name as a fallback
      return foundUser ? foundUser.id : selectedName;
    });

    // 2. Extra safety check for "All"
    let finalUsers = userIds;
    if (finalUsers.length > 1 && finalUsers.includes("All")) {
      finalUsers = finalUsers.filter(id => id !== "All");
      formik.setFieldValue("users", finalUsers); // Update UI state
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        Users: finalUsers, // Send the array directly
        Id: currentCommunityId,
      }),
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Invalid credentials or permissions.');
      }
      throw new Error('Network response was not ok');
    }
    return response.json();
  };

  const removeCommunityMemberMutation = useMutation({
    mutationFn: removeCommunityMember,
    onSuccess: (data) => {
      if (data) {
        handleClose();
      } else {
        throw new Error('Unexpected response structure');
      }
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  // --- DIALOG CONTROL ---
  const handleClickOpen = (e) => {
    e.stopPropagation(); // Prevent Menu closing until we want it to
    setOpen(true);
  };

  const handleClose = () => {
    setError(null);
    setOpen(false);
    if (closeMenu) closeMenu();
  };

  const formik = useFormik({
    initialValues: {
      users: ['All'],
    },
    validationSchema: Yup.object({
      users: Yup.array()
        .of(Yup.string())
        .min(1, 'At least one user is required')
        .required('At least one user is required'),
    }),
    onSubmit: (values) => {
      setError(null);
      removeCommunityMemberMutation.mutate(values);
    },
  });

  return (
    <div>
      <Button
        id="removeCommunityMemberButton"
        variant="contained"
        color="none"
        onClick={handleClickOpen}
        style={{ boxShadow: 'none' }}
      >
        Remove Member
      </Button>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Remove Community Members</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>

            <GetUsers formik={formik} setName={setName} currentUser={currentUser} />

            {/* Removing community field */}
            <GetUserCommunities formik={formik} />

            {error && (
              <Typography color="error" variant="body2" style={{ marginTop: '10px' }}>
                {error}
              </Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={removeCommunityMemberMutation.isLoading}>
              {removeCommunityMemberMutation.isLoading ? 'Removing members...' : 'Remove members'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default RemoveCommunityMember;